/* ─── Airport-style session start announcer ───
 * Plays a 2-tone chime via Web Audio API, then speaks an announcement
 * via the SpeechSynthesis API. No external files / network required.
 *
 * Browsers block audio until the first user gesture, so call `unlock()`
 * once from inside a click handler before relying on auto-announcements.
 * armAnnouncerUnlock() does that on the first click or key press anywhere.
 */

const VOLUME_STORAGE_KEY = "vistaar_session_announcer_volume";
const DEFAULT_VOLUME = 1;

let audioCtx = null;
let masterGain = null;
let unlocked = false;
let chimePlayingUntil = 0; // ctx.currentTime (sec) when the last chime ends

const readStoredVolume = () => {
  if (typeof window === "undefined") return DEFAULT_VOLUME;
  const raw = parseFloat(localStorage.getItem(VOLUME_STORAGE_KEY));
  if (Number.isFinite(raw) && raw >= 0 && raw <= 1) return raw;
  return DEFAULT_VOLUME;
};

const getAudioContext = () => {
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = readStoredVolume();
    masterGain.connect(audioCtx.destination);
  }
  return audioCtx;
};

/** Returns current master volume (0..1). Reads from storage if context not yet built. */
export const getAnnouncerVolume = () => {
  if (masterGain) return masterGain.gain.value;
  return readStoredVolume();
};

/** Updates master volume (0..1) immediately and persists to localStorage. */
export const setAnnouncerVolume = (v) => {
  const clamped = Math.max(0, Math.min(1, Number(v) || 0));
  localStorage.setItem(VOLUME_STORAGE_KEY, String(clamped));
  if (masterGain && audioCtx) {
    // setTargetAtTime for smooth ramp without clicks
    masterGain.gain.setTargetAtTime(clamped, audioCtx.currentTime, 0.01);
  }
};

/** Call this from inside a user gesture (click) to permit audio playback. */
export const unlockAnnouncer = () => {
  const ctx = getAudioContext();
  if (ctx && ctx.state === "suspended") ctx.resume();
  const wasUnlocked = unlocked;
  unlocked = true;
  if (!wasUnlocked && typeof window !== "undefined") {
    window.dispatchEvent(new Event("vistaar:announcer-unlocked"));
  }
};

export const isAnnouncerUnlocked = () => unlocked;

// Events a browser counts as a user gesture that may start audio (not touchstart).
const GESTURE_EVENTS = ["pointerdown", "keydown", "click", "touchend"];

/**
 * Unlock the audio on the first click or key press anywhere on the page.
 *
 * The chime is ON by default and the setting survives a reload, but the unlock did
 * not: it happened only when the sound button was switched on (or the test chime
 * played). After any reload the button showed ON while every chime was skipped as
 * "still locked", until someone muted and unmuted it. Returns a cleanup function.
 */
export const armAnnouncerUnlock = () => {
  if (unlocked || typeof document === "undefined") return () => {};
  const onGesture = () => {
    unlockAnnouncer();
    disarm();
  };
  const disarm = () => GESTURE_EVENTS.forEach((e) => document.removeEventListener(e, onGesture, true));
  GESTURE_EVENTS.forEach((e) => document.addEventListener(e, onGesture, true));
  return disarm;
};

/**
 * Airport-style 3-note "bong-bing-bong" chime with bell-like harmonics.
 * Each note layers a fundamental + 2nd harmonic (slightly detuned) for resonance.
 * Uses a single shared master gain (created once in getAudioContext) so that
 * repeated toggles don't accumulate orphaned graph nodes, and waits for the
 * AudioContext to actually resume before scheduling so timing stays accurate.
 * Returns total duration in ms.
 */
export const playChime = () => {
  const ctx = getAudioContext();
  if (!ctx || !masterGain) return 0;

  const schedule = () => {
    const now = ctx.currentTime;
    // Don't overlap with a chime already in flight — silently skip
    if (now < chimePlayingUntil) return;

    const playBell = (freq, startOffset, sustain) => {
      const start = now + startOffset;
      const end = start + sustain;

      // Fundamental
      const osc1 = ctx.createOscillator();
      const g1 = ctx.createGain();
      osc1.type = "sine";
      osc1.frequency.value = freq;
      g1.gain.setValueAtTime(0, start);
      g1.gain.linearRampToValueAtTime(0.55, start + 0.015);
      g1.gain.exponentialRampToValueAtTime(0.0001, end);
      osc1.connect(g1).connect(masterGain);
      osc1.start(start);
      osc1.stop(end + 0.05);
      osc1.onended = () => { try { osc1.disconnect(); g1.disconnect(); } catch (_) {} };

      // 2nd harmonic — adds bell shimmer
      const osc2 = ctx.createOscillator();
      const g2 = ctx.createGain();
      osc2.type = "sine";
      osc2.frequency.value = freq * 2.01;
      g2.gain.setValueAtTime(0, start);
      g2.gain.linearRampToValueAtTime(0.18, start + 0.02);
      g2.gain.exponentialRampToValueAtTime(0.0001, start + sustain * 0.7);
      osc2.connect(g2).connect(masterGain);
      osc2.start(start);
      osc2.stop(end + 0.05);
      osc2.onended = () => { try { osc2.disconnect(); g2.disconnect(); } catch (_) {} };
    };

    // F#5 → D5 → F#5 (classic descending airport pattern resolving back up)
    playBell(739.99, 0.00, 1.20);
    playBell(587.33, 0.55, 1.40);
    playBell(739.99, 1.20, 1.80);
    chimePlayingUntil = now + 3.05;
  };

  // Wait for resume to complete before scheduling — otherwise currentTime is
  // stale and notes pile up at the wrong offsets on repeat toggles.
  if (ctx.state === "suspended") {
    ctx.resume().then(schedule);
  } else {
    schedule();
  }
  return 2600;
};

/**
 * Speak a single line using SpeechSynthesis. Soft, warm, PA-announcement style.
 * Voice selection prefers known-soft female voices across macOS / Windows / Chrome.
 */
export const speak = (text, { rate = 0.78, pitch = 0.92, volume = 0.9, lang = "en-IN" } = {}) => {
  if (!window.speechSynthesis) return;
  const utter = new SpeechSynthesisUtterance(text);
  utter.rate = rate;
  utter.pitch = pitch;
  utter.volume = volume;
  utter.lang = lang;

  // Priority list of soft/warm voices known to sound gentle and clear.
  // Names vary by OS: macOS uses one set, Windows uses another, Chrome adds Google voices.
  const SOFT_VOICE_PATTERNS = [
    // Soft, warm English female voices (macOS / iOS / Chrome / Edge)
    /samantha/i,         // macOS US — warm, soft
    /serena/i,           // macOS UK — gentle
    /karen/i,            // macOS AU — calm
    /tessa/i,            // macOS ZA — soft
    /moira/i,            // macOS IE — gentle
    /fiona/i,            // macOS Scottish — soft
    /allison/i,          // Apple Siri — soft
    /ava/i,              // Apple Siri premium — very soft
    /susan/i,            // macOS
    // Microsoft / Windows neural voices
    /aria(?!.*male)/i,   // Microsoft Aria — neutral soft
    /jenny/i,            // Microsoft Jenny — warm
    /sonia/i,            // Microsoft UK — soft
    /natasha/i,          // Microsoft AU — soft
    // Google (Chrome) voices
    /google uk english female/i,
    /google us english/i,
    // Indian female voices (if installed)
    /veena/i,            // macOS Indian English
    /heera/i,            // Windows Hindi female
    /rishi/i,            // macOS Indian English (male but warm — fallback)
  ];

  const voices = window.speechSynthesis.getVoices();
  let chosen = null;
  // Walk priority list in order, picking first installed voice
  for (const pattern of SOFT_VOICE_PATTERNS) {
    chosen = voices.find(v => pattern.test(v.name));
    if (chosen) break;
  }
  // Fallbacks: any female-tagged English voice, then any en-IN, then en-US/GB
  if (!chosen) {
    chosen = voices.find(v => /female/i.test(v.name) && /en/i.test(v.lang))
      || voices.find(v => /en-IN/i.test(v.lang))
      || voices.find(v => /en-US|en-GB/i.test(v.lang));
  }
  if (chosen) utter.voice = chosen;
  window.speechSynthesis.speak(utter);
};

/**
 * Announce a session start: just the chime, no voice.
 * @param {object} _session  (unused — kept for API stability)
 */
export const announceSession = (_session) => {
  playChime();
};

/**
 * Plays the chime twice in a row (~3.2s apart) for stronger confirmation
 * feedback — e.g. the manual test button, session-completion. The gap
 * matches the chime's own duration so the second play isn't silently
 * skipped by playChime's overlap guard.
 */
export const announceTwice = (session) => {
  announceSession(session);
  setTimeout(() => announceSession(session), 3200);
};
